import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiArrowRight, FiInbox, FiPlus, FiSearch, FiClock } from 'react-icons/fi';
import { formatarDataBR } from '../../lib/data';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useAuth } from '../../contexts/AuthContext';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import { solicitacoesService, type SolicitacaoResumo } from '../../services/solicitacoes.service';

const STATUS_LABEL: Record<string, string> = {
  rascunho: 'Rascunho',
  aberto: 'Aberto',
  em_tramitacao: 'Em tramitação',
  aguardando_solicitante: 'Aguardando você',
  aguardando_aceite: 'Aguardando aceite',
  em_analise: 'Em análise',
  em_diligencia: 'Em diligência',
  deferido: 'Deferido',
  indeferido: 'Indeferido',
  arquivado: 'Arquivado',
  cancelado: 'Cancelado',
  concluido: 'Concluído',
};

const STATUS_COR: Record<string, string> = {
  aguardando_solicitante: 'bg-amber-50 text-amber-700 border-amber-200',
  em_tramitacao: 'bg-blue-50 text-blue-700 border-blue-200',
  em_analise: 'bg-blue-50 text-blue-700 border-blue-200',
  em_diligencia: 'bg-blue-50 text-blue-700 border-blue-200',
  aberto: 'bg-blue-50 text-blue-700 border-blue-200',
  deferido: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  concluido: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  indeferido: 'bg-red-50 text-red-700 border-red-200',
  arquivado: 'bg-slate-100 text-slate-600 border-slate-200',
  cancelado: 'bg-slate-100 text-slate-600 border-slate-200',
  rascunho: 'bg-slate-100 text-slate-600 border-slate-200',
};

const ABAS = [
  { valor: 'abertas', label: 'Em andamento' },
  { valor: 'aguardando', label: 'Aguardando você' },
  { valor: 'finalizadas', label: 'Finalizadas' },
  { valor: 'todas', label: 'Todas' },
];

export function MinhasSolicitacoesPage() {
  const { cidadao } = useAuth();
  const [busca, setBusca] = useState('');
  const [aba, setAba] = useState('abertas');

  const { dados, carregando } = useDadosVivos<SolicitacaoResumo[]>(
    () => solicitacoesService.listarMinhas(cidadao!.id),
    [cidadao?.id],
    { ativo: !!cidadao },
  );
  const solicitacoes = dados ?? [];

  const termo = busca.trim().toLowerCase();
  const filtradas = solicitacoes.filter((s) => {
    if (termo && !s.titulo.toLowerCase().includes(termo) && !s.numero.toLowerCase().includes(termo)) {
      return false;
    }
    if (aba === 'abertas') {
      return ['aberto', 'em_tramitacao', 'em_analise', 'em_diligencia'].includes(s.status);
    }
    if (aba === 'aguardando') {
      return s.status === 'aguardando_solicitante';
    }
    if (aba === 'finalizadas') {
      return ['concluido', 'arquivado', 'deferido', 'indeferido', 'cancelado'].includes(s.status);
    }
    return true;
  });

  const aguardandoVoce = solicitacoes.filter((s) => s.status === 'aguardando_solicitante').length;

  return (
    <PortalPage
      titulo="Minhas solicitações"
      subtitulo="Acompanhe o andamento dos protocolos que você abriu."
      icone={<FiFileText size={26} />}
      largura="larga"
      voltarHref="/"
      acoes={
        <Button onClick={() => window.location.assign('/servicos')}>
          <FiPlus size={14} /> Nova solicitação
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="max-w-md relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <Input
            type="search"
            placeholder="Buscar por número ou assunto..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex border-b border-slate-200 gap-1">
          {ABAS.map((a) => (
            <button
              key={a.valor}
              onClick={() => setAba(a.valor)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                aba === a.valor
                  ? 'border-institutional text-institutional'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {a.label}
              {a.valor === 'aguardando' && aguardandoVoce > 0 && (
                <span className="ml-1.5 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                  {aguardandoVoce}
                </span>
              )}
            </button>
          ))}
        </div>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : solicitacoes.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm text-slate-700 font-medium">Você ainda não tem solicitações.</p>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Comece abrindo um protocolo para a prefeitura.
              </p>
              <Button onClick={() => window.location.assign('/servicos')}>
                <FiPlus size={14} /> Ver carta de serviços
              </Button>
            </div>
          </SectionCard>
        ) : filtradas.length === 0 ? (
          <SectionCard>
            <div className="text-center py-10">
              <p className="text-sm text-slate-600">
                Nenhuma solicitação nesta aba {termo && <span>para "{busca}"</span>}.
              </p>
            </div>
          </SectionCard>
        ) : (
          <SectionCard semPadding>
            <ul className="divide-y divide-slate-100">
              {filtradas.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/solicitacao/${s.numero}`}
                    className="flex items-start gap-4 p-4 hover:bg-slate-50 transition"
                  >
                    <FiFileText className="text-slate-400 flex-shrink-0 mt-0.5" size={18} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 tabular-nums">{s.numero}</span>
                        <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded border ${STATUS_COR[s.status] ?? STATUS_COR.rascunho}`}>
                          {STATUS_LABEL[s.status] ?? s.status}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 truncate">{s.titulo}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                        {s.assunto?.nome && <span>{s.assunto.nome}</span>}
                        {s.setor_atual?.nome && <span>· {s.setor_atual.nome}</span>}
                        {s.data_limite && (
                          <span className="flex items-center gap-1">
                            <FiClock size={11} /> Prazo {formatarDataBR(s.data_limite)}
                          </span>
                        )}
                      </div>
                    </div>
                    <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
                  </Link>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}
      </div>
    </PortalPage>
  );
}
